import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Dialog as MuiDialog,
  DialogContent,
  DialogActions,
  IconButton,
  DialogTitle,
  Button,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CenteredFlexBox, ColumnFlexBox } from '../FlexBox';

const Dialog = function Dialog({
  id,
  open = false,
  onClose = () => {},
  children,
  title = '',
  titleColor = '',
  styles,
  icon = undefined,
  width = 'md',
  fullScreen = false,
  cancelLabel = 'CANCEL',
  submitLabel = 'CONFIRM',
  actionPosition = 'flex-end',
  onSubmit,
  onCancel,
  form = false,
  disableSubmit = false,
  dividers = false,
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  hideCloseIcon = false,
  padding = '16px',
  fullWidth,
  hideSubmitButton = false,
  overTitle,
  leftAction,
}) {
  const intl = useIntl();
  const methods = useForm();

  useEffect(() => {
    if (!open) {
      const defaultValues = methods.getValues();
      Object.keys(defaultValues)?.forEach((key) => {
        methods.setValue(key, null);
      });
    }
  }, [open]);

  const translate = (txt) => {
    return intl.messages[txt?.toUpperCase()] ?? txt;
  };

  // eslint-disable-next-line no-unused-vars
  const onError = (err, e) => {
    console.log(err);
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { onClose });
    }
    return child;
  });
  const actionButtons = (
    <>
      {onCancel && (
        <Button
          id="dialog-cancel-btn"
          disableElevation
          variant="outlined"
          sx={{ width: '200px' }}
          onClick={onCancel}
        >
          {cancelLabel && translate(cancelLabel)}
        </Button>
      )}
      {onSubmit && !hideSubmitButton && (
        <Button
          id="dialog-submit-btn"
          disableElevation
          type={form ? 'submit' : 'button'}
          sx={{ width: '200px', whiteSpace: 'nowrap' }}
          disabled={disableSubmit}
          onClick={
            form
              ? undefined
              : (e) => {
                  e.preventDefault();
                  if (!form) onSubmit();
                }
          }
        >
          {submitLabel && translate(submitLabel)}
        </Button>
      )}
    </>
  );

  const FormContent = (
    <>
      <DialogContent
        dividers={dividers}
        sx={{
          display: 'flex',
          padding,
          ...styles?.content,
        }}
      >
        {childrenWithProps}
      </DialogContent>
      {((onSubmit && !hideSubmitButton) || onCancel) && (
        <DialogActions
          sx={{
            display: 'flex',
            alignItems: 'center',
            // A left action owns the far side of the bar, so the two buttons
            // have to travel together rather than spread out with it.
            justifyContent: leftAction ? 'space-between' : actionPosition,
            gap: '12px',
            padding: '12px',
            ...styles?.actions,
          }}
        >
          {leftAction ? (
            <>
              {leftAction}
              <CenteredFlexBox gap="12px">{actionButtons}</CenteredFlexBox>
            </>
          ) : (
            actionButtons
          )}
        </DialogActions>
      )}
    </>
  );

  return (
    <FormProvider {...methods}>
      <MuiDialog
        id={id}
        open={open}
        fullScreen={fullScreen}
        // MUI 9 has no disableEscapeKeyDown: Esc reaches onClose with its reason, like the backdrop.
        // The form's dirty state goes along so the page can ask before dropping what was typed.
        onClose={(event, reason) => {
          if (reason === 'escapeKeyDown' && disableEscapeKeyDown) return;
          if (reason === 'backdropClick' && disableBackdropClick) return;
          onClose(event, methods.formState.isDirty);
        }}
        maxWidth={width}
        fullWidth={fullWidth}
        sx={{
          ...styles?.root,
          '& .MuiDialog-paper': { borderRadius: fullScreen ? 'unset' : '18px' },
        }}
        transitionDuration={500}
      >
        {title && (
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              userSelect: 'none',
              ...styles?.title,
            }}
          >
            {icon}
            <ColumnFlexBox flexGrow={1}>
              <Typography variant="button" color="text.secondary">
                {translate(overTitle)}
              </Typography>
              {typeof title === 'string' ? (
                <Typography
                  variant="h6"
                  color={titleColor ?? 'inherit'}
                  sx={{ fontSize: '24px' }}
                >
                  {translate(title)}
                </Typography>
              ) : (
                title
              )}
            </ColumnFlexBox>
            {!hideCloseIcon && (
              <IconButton
                onClick={(e) => onClose(e, methods.formState.isDirty)}
                color="inherit"
                sx={{ marginLeft: 'auto' }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
        )}

        {form ? (
          <form
            style={{
              display: 'contents', //contents does not creates form element in DOM
            }}
            onSubmit={methods?.handleSubmit(onSubmit, onError)}
            noValidate
          >
            {FormContent}
          </form>
        ) : (
          FormContent
        )}
      </MuiDialog>
    </FormProvider>
  );
};

export default Dialog;
